import { createFeature, createReducer, createSelector, on } from '@ngrx/store';

import { AdvertisementDetailDto, AdvertisementsListDto, AdvertisementSuggestionsDto } from '../api';
import {
  initialStateResult,
  ResultState,
  selectIsStateLoading,
  selectResult,
  stateError,
  stateLoading,
  stateSuccess,
} from '../shared/ngrx';
import {
  getAdvertisement,
  getAdvertisementFailed,
  getAdvertisementSuccess,
  listAdvertisementSuggestions,
  listAdvertisementSuggestionsFailed,
  listAdvertisementSuggestionsSuccess,
  searchAdvertisements,
  searchAdvertisementsFailed,
  searchAdvertisementsSuccess,
  sendContactRequest,
  sendContactRequestFailed,
  sendContactRequestSuccess,
} from './advertisements.actions';

interface State {
  advertisementSuggestions: ResultState<AdvertisementSuggestionsDto>;
  advertisements: ResultState<AdvertisementsListDto>;
  advertisementDetails: ResultState<AdvertisementDetailDto>;
  advertisementContactRequest: ResultState<null>;
}

const initialState: State = {
  advertisementSuggestions: initialStateResult,
  advertisements: initialStateResult,
  advertisementDetails: initialStateResult,
  advertisementContactRequest: initialStateResult,
};

export const advertisementsFeature = createFeature({
  name: 'advertisements',
  reducer: createReducer(
    initialState,
    on(listAdvertisementSuggestions, (state): State => ({
      ...state,
      advertisementSuggestions: stateLoading(state.advertisementSuggestions.result),
    })),
    on(listAdvertisementSuggestionsSuccess, (state, action): State => ({
      ...state,
      advertisementSuggestions: stateSuccess(action.payload),
    })),
    on(listAdvertisementSuggestionsFailed, (state, action): State => ({
      ...state,
      advertisementSuggestions: stateError(action.error, state.advertisementSuggestions.result),
    })),
    on(searchAdvertisements, (state): State => ({
      ...state,
      advertisements: stateLoading(state.advertisements.result),
    })),
    on(searchAdvertisementsSuccess, (state, action): State => ({
      ...state,
      advertisements: stateSuccess(action.payload),
    })),
    on(searchAdvertisementsFailed, (state, action): State => ({
      ...state,
      advertisements: stateError(action.error, state.advertisements.result),
    })),
    on(getAdvertisement, (state): State => ({
      ...state,
      advertisementDetails: stateLoading(initialState.advertisementDetails.result),
    })),
    on(getAdvertisementSuccess, (state, action): State => ({
      ...state,
      advertisementDetails: stateSuccess(action.payload),
    })),
    on(getAdvertisementFailed, (state, action): State => ({
      ...state,
      advertisementDetails: stateError(action.error, initialState.advertisementDetails.result),
    })),
    on(sendContactRequest, (state): State => ({
      ...state,
      advertisementContactRequest: stateLoading(initialState.advertisementContactRequest.result),
    })),
    on(sendContactRequestSuccess, (state): State => ({
      ...state,
      advertisementContactRequest: stateSuccess(null),
    })),
    on(sendContactRequestFailed, (state, action): State => ({
      ...state,
      advertisementContactRequest: stateError(action.error, initialState.advertisementContactRequest.result),
    })),
  ),
});

export const selectAdvertisementSuggestions = createSelector(
  advertisementsFeature.selectAdvertisementSuggestions,
  selectResult,
);

export const selectAdvertisementSuggestionsLoading = createSelector(
  advertisementsFeature.selectAdvertisementSuggestions,
  selectIsStateLoading,
);

export const selectAdvertisementsList = createSelector(
  advertisementsFeature.selectAdvertisements,
  (s): AdvertisementsListDto =>
    s.result ?? {
      results: [],
      suggestions: [],
      page: 0,
      page_size: 0,
      total: 0,
    },
);
export const selectAdvertisementsLoading = createSelector(
  advertisementsFeature.selectAdvertisements,
  selectIsStateLoading,
);

export const selectAdvertisementDetails = createSelector(
  advertisementsFeature.selectAdvertisementDetails,
  selectResult,
);
export const selectAdvertisementDetailsLoading = createSelector(
  advertisementsFeature.selectAdvertisementDetails,
  selectIsStateLoading,
);

export const selectAdvertisementRequestLoading = createSelector(
  advertisementsFeature.selectAdvertisementContactRequest,
  selectIsStateLoading,
);
